import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'
import { type Language } from '@/i18n/translations'

export default function Login() {
  const { lang, setLang, T } = useLanguage()
  const [params] = useSearchParams()
  const error = params.get('error')

  // Where the visitor was headed before the guard bounced them here. It rides
  // to Discord and back in `state`, so a DM link to one match survives a login.
  const next = params.get('next')
  const loginHref = next && next.startsWith('/') && !next.startsWith('//')
    ? `/api/auth/login?next=${encodeURIComponent(next)}`
    : '/api/auth/login'

  const cardRef = useRef<HTMLDivElement>(null)
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, opacity: 0 })

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setSpotlight({ x, y, opacity: 1 })
  }

  const handleMouseLeave = () => {
    setSpotlight(s => ({ ...s, opacity: 0 }))
  }

  const errorMsg = error
    ? (T.login.errors[error as keyof typeof T.login.errors] ?? T.login.errors.generic)
    : null

  const copyright = T.login.copyright.replace('{year}', String(new Date().getFullYear()))

  return (
    <div className="relative flex h-screen items-center justify-center overflow-hidden bg-background">

      {/* Background image */}
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/background.webp)' }}
      />
      {/* Neutral scrim, matching the app shell */}
      <div className="pointer-events-none absolute inset-0 bg-black/45 backdrop-blur-[2px]" />

      {/* Language switcher — top right */}
      <div className="absolute top-4 right-4 z-10">
        <div className="relative">
          <select
            value={lang}
            onChange={e => setLang(e.target.value as Language)}
            className="appearance-none rounded-sm bg-card/70 border border-border text-foreground/80 text-[11px] font-semibold tracking-wider pl-3 pr-7 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer backdrop-blur-sm hover:bg-card transition-colors"
          >
            <option value="en">EN</option>
            <option value="tr">TR</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      <div className="relative flex flex-col items-center gap-8">

        {/* Wordmark. The badge itself is the photograph behind this. */}
        <div className="text-center">
          <h1 className="text-5xl font-extrabold uppercase tracking-tight">Turks Let Loose</h1>
          <p className="text-sm text-muted-foreground mt-1">Dashboard</p>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="relative w-80 rounded-md border border-border bg-card/70 backdrop-blur-md p-6 flex flex-col items-center gap-5 overflow-hidden"
        >
          {/* Spotlight */}
          <div
            className="pointer-events-none absolute inset-0 transition-opacity duration-300"
            style={{
              opacity: spotlight.opacity,
              background: `radial-gradient(300px circle at ${spotlight.x}% ${spotlight.y}%, rgba(239,224,216,0.06), transparent 70%)`,
            }}
          />

          {errorMsg && (
            <div className="w-full rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive text-center">
              {errorMsg}
            </div>
          )}

          <div className="text-center">
            <p className="text-base font-semibold">{T.login.signInTitle}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {T.login.discordDesc}
            </p>
          </div>

          <a href={loginHref} className="w-full">
            <Button
              size="lg"
              className="w-full gap-3 active:scale-95 font-semibold transition-all duration-100 cursor-pointer select-none"
            >
              <svg width="20" height="20" viewBox="0 0 71 55" fill="currentColor">
                <path d="M60.1 4.9A58.6 58.6 0 0 0 45.6.3a.2.2 0 0 0-.2.1 40.8 40.8 0 0 0-1.8 3.7 54.1 54.1 0 0 0-16.2 0A37.4 37.4 0 0 0 25.6.4a.2.2 0 0 0-.2-.1A58.4 58.4 0 0 0 10.9 4.9a.2.2 0 0 0-.1.1C1.6 18.1-.9 31 .3 43.6a.2.2 0 0 0 .1.2 58.9 58.9 0 0 0 17.7 8.9.2.2 0 0 0 .2-.1 42 42 0 0 0 3.6-5.9.2.2 0 0 0-.1-.3 38.7 38.7 0 0 1-5.5-2.6.2.2 0 0 1 0-.4c.4-.3.7-.6 1.1-.9a.2.2 0 0 1 .2 0c11.5 5.3 24 5.3 35.4 0a.2.2 0 0 1 .2 0l1.1.9a.2.2 0 0 1 0 .4 36.3 36.3 0 0 1-5.5 2.6.2.2 0 0 0-.1.3 47.1 47.1 0 0 0 3.6 5.9.2.2 0 0 0 .2.1 58.7 58.7 0 0 0 17.8-8.9.2.2 0 0 0 .1-.2c1.4-14.6-2.4-27.3-10.5-38.6a.2.2 0 0 0-.1-.1zM23.7 36c-3.5 0-6.4-3.2-6.4-7.1 0-4 2.8-7.2 6.4-7.2 3.6 0 6.5 3.3 6.4 7.2 0 3.9-2.9 7.1-6.4 7.1zm23.7 0c-3.5 0-6.4-3.2-6.4-7.1 0-4 2.8-7.2 6.4-7.2 3.6 0 6.5 3.3 6.4 7.2 0 3.9-2.8 7.1-6.4 7.1z" />
              </svg>
              {T.login.continueWithDiscord}
            </Button>
          </a>

          {/* OAuth2 badge */}
          <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground/60">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {T.login.securedOAuth}
          </div>

        </div>
      </div>

      {/* Footer */}
      <p className="absolute bottom-8 text-[12px] text-muted-foreground/70 select-none">
        {copyright}
      </p>

    </div>
  )
}
